import { Add, Delete, Edit, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, Link, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import AdvancedFilters from './AdvancedFilters';
import SearchField from './SearchField';
import StoragePickerDialog from './StoragePickerDialog';
import StyledTableContainer from './StyledTableContainer';

const SectionItems = ({ section }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [presignedMap, setPresignedMap] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerImagesOnly, setPickerImagesOnly] = useState(false);
  const [pickerTargetField, setPickerTargetField] = useState(null);

  // Сортировка
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    if (section) {
      fetchItems();
    }
  }, [section]);

  useEffect(() => {
    if (items.length > 0) {
      let filtered = items;

      // Применяем поиск
      if (searchQuery) {
        filtered = filtered.filter(item => {
          return section.schema.some(field => {
            const value = item[field.name];
            if (value === null || value === undefined) return false;

            const stringValue = typeof value === 'boolean'
              ? (value ? 'да' : 'нет')
              : String(value).toLowerCase();

            return stringValue.includes(searchQuery.toLowerCase());
          });
        });
      }

      // Применяем расширенные фильтры
      if (advancedFilters.length > 0) {
        filtered = filtered.filter(item => {
          return advancedFilters.every(filter => {
            const value = item[filter.field];
            if (value === null || value === undefined) return false;

            const stringValue = typeof value === 'boolean'
              ? (value ? 'да' : 'нет')
              : String(value).toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case 'contains':
                return stringValue.includes(filterValue);
              case 'equals':
                return stringValue === filterValue;
              case 'startsWith':
                return stringValue.startsWith(filterValue);
              case 'endsWith':
                return stringValue.endsWith(filterValue);
              case 'greaterThan':
                return Number(value) > Number(filter.value);
              case 'lessThan':
                return Number(value) < Number(filter.value);
              default:
                return true;
            }
          });
        });
      }

      // Применяем сортировку
      if (sortBy) {
        filtered = filtered.sort((a, b) => {
          let aValue = a[sortBy];
          let bValue = b[sortBy];

          // Обработка пустых значений
          if (aValue === null || aValue === undefined) return 1;
          if (bValue === null || bValue === undefined) return -1;

          // Сравнение
          if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue?.toLowerCase() || '';
          }

          if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      setFilteredItems(filtered);
    }
  }, [searchQuery, items, section.schema, advancedFilters, sortBy, sortOrder]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/sections/${section.id}/items`);
      setItems(response.data);
      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке элементов:', err);
      setError('Не удалось загрузить список элементов');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await apiClient.put(`/sections/${section.id}/items/${editingItem._id}`, formData);
      } else {
        await apiClient.post(`/sections/${section.id}/items`, formData);
      }
      handleCloseDialog();
      fetchItems();
    } catch (err) {
      console.error('Ошибка при сохранении элемента:', err);
      setError('Не удалось сохранить элемент');
    }
  };

  const uploadForField = async (fieldName, fileObj) => {
    try {
      const form = new FormData();
      form.append('file', fileObj);
      // Сохраняем файлы в префиксе по секции
      form.append('prefix', `sections/${section.id}/`);
      const resp = await apiClient.post('/storage/files', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const key = resp.data?.key;
      if (key) {
        handleChange(fieldName, key);
      }
    } catch (e) {
      console.error('Ошибка загрузки файла', e);
      setError('Ошибка загрузки файла');
    }
  };

  const openPicker = (field, isImage) => {
    setPickerTargetField(field?.name);
    setPickerImagesOnly(!!isImage);
    setPickerOpen(true);
  };

  const handlePickerSelect = (key) => {
    if (pickerTargetField && key) {
      handleChange(pickerTargetField, key);
    }
    setPickerOpen(false);
    setPickerTargetField(null);
  };

  const fetchPresigned = async (key) => {
    if (!key || presignedMap[key]) return presignedMap[key];
    try {
      const resp = await apiClient.get(`/storage/files/${encodeURIComponent(key)}/url`);
      const url = resp.data?.url;
      if (url) {
        setPresignedMap(prev => ({ ...prev, [key]: url }));
        return url;
      }
    } catch (e) {
      console.error('Ошибка получения ссылки на файл', e);
    }
    return null;
  };

  const renderFileField = (field, isImage) => {
    const key = formData[field.name];
    const fileName = key ? key.split('/').pop() : '';
    return (
      <Box>
        {key ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isImage ? (
              <Box sx={{ width: 120, height: 80, borderRadius: 1, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Превью через пресайн */}
                {presignedMap[key] ? (
                  <img src={presignedMap[key]} alt={fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                ) : (
                  <Button size="small" variant="outlined" onClick={async () => { await fetchPresigned(key); }}>
                    Показать превью
                  </Button>
                )}
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{fileName}</Typography>
              <Button size="small" onClick={async () => {
                const url = await fetchPresigned(key);
                if (url) window.open(url, '_blank');
              }}>Открыть</Button>
            </Box>
            <Button variant="outlined" size="small" component="label">
              Заменить файл
              <input type="file" hidden onChange={(e) => e.target.files && e.target.files[0] && uploadForField(field.name, e.target.files[0])} />
            </Button>
            <Button variant="text" size="small" onClick={() => openPicker(field, isImage)}>
              Выбрать из хранилища
            </Button>
          </Box>
        ) : (
          <Button variant="outlined" size="small" component="label">
            Загрузить {isImage ? 'изображение' : 'файл'}
            <input type="file" accept={isImage ? 'image/*' : undefined} hidden onChange={(e) => e.target.files && e.target.files[0] && uploadForField(field.name, e.target.files[0])} />
          </Button>
        )}
        {!key && (
          <Button sx={{ ml: 2 }} variant="text" size="small" onClick={() => openPicker(field, isImage)}>
            Выбрать из хранилища
          </Button>
        )}
      </Box>
    );
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот элемент?')) {
      try {
        await apiClient.delete(`/sections/${section.id}/items/${itemId}`);
        fetchItems();
      } catch (err) {
        console.error('Ошибка при удалении элемента:', err);
        setError('Не удалось удалить элемент');
      }
    }
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => {
    setAdvancedFilters([]);
  };

  const filterFields = section.schema.map(field => ({
    value: field.name,
    label: field.name,
  }));

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={field.name}
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            margin="normal"
            required
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.name}
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, Number(e.target.value))}
            margin="normal"
            required
          />
        );
      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            label={field.name}
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={formData[field.name] || false}
                onChange={(e) => handleChange(field.name, e.target.checked)}
              />
            }
            label={field.name}
          />
        );
      case 'select':
        return (
          <FormControl fullWidth margin="normal">
            <InputLabel>{field.name}</InputLabel>
            <Select
              value={formData[field.name] || ''}
              label={field.name}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required
            >
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'file':
        return renderFileField(field, false);
      case 'image':
        return renderFileField(field, true);
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{
        p: 3,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
      }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h2">
            {section.name}
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Добавить элемент
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={`Поиск в разделе "${section.name}"...`}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <AdvancedFilters
            filters={advancedFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            fields={filterFields}
          />
        </Box>

        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Сортировать по</InputLabel>
            <Select
              value={sortBy}
              label="Сортировать по"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="">
                <em>Без сортировки</em>
              </MenuItem>
              {section.schema.map((field) => (
                <MenuItem key={field.name} value={field.name}>
                  {field.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {sortBy && (
            <IconButton
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              sx={{
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                }
              }}
            >
              {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
            </IconButton>
          )}
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            {error}
          </Paper>
        )}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {section.schema.map((field) => (
                  <TableCell sx={{ color: 'white' }} key={field.name}>{field.name}</TableCell>
                ))}
                <TableCell sx={{ color: 'white', position: 'sticky', right: 0, background: 'rgba(30,30,30,0.8)', zIndex: 2 }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={section.schema.length + 1} align="center">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={section.schema.length + 1} align="center">
                    {searchQuery ? 'Элементы не найдены' : 'Элементы не найдены'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => (
                  <TableRow key={item._id}>
                    {section.schema.map((field) => (
                      <TableCell sx={{ color: 'white' }} key={field.name}>
                        {field.type === 'boolean' ? (
                          item[field.name] ? 'Да' : 'Нет'
                        ) : field.type === 'image' ? (
                          item[field.name] ? (
                            <img
                              alt={field.name}
                              src={presignedMap[item[field.name]] || ''}
                              style={{ maxWidth: 80, maxHeight: 60, objectFit: 'cover' }}
                              onMouseEnter={async () => { if (!presignedMap[item[field.name]]) await fetchPresigned(item[field.name]); }}
                            />
                          ) : '—'
                        ) : field.type === 'file' ? (
                          item[field.name] ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={async () => {
                                const url = await fetchPresigned(item[field.name]);
                                if (url) window.open(url, '_blank');
                              }}
                            >
                              {String(item[field.name]).split('/').pop()}
                            </Link>
                          ) : '—'
                        ) : (
                          item[field.name] || '—'
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{ position: 'sticky', right: 0, background: 'rgba(30,30,30,0.7)', zIndex: 1 }}>
                      <IconButton sx={{ color: 'white' }} onClick={() => handleOpenDialog(item)}>
                        <Edit />
                      </IconButton>
                      <IconButton sx={{ color: 'white' }} onClick={() => handleDelete(item._id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Редактировать элемент' : 'Добавить элемент'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {section.schema.map((field) => (
              <Box key={field.name} sx={{ mb: 2 }}>
                {renderField(field)}
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>

      <StoragePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        imagesOnly={pickerImagesOnly}
        initialPrefix={`sections/${section?.id || ''}`}
      />
    </Box>
  );
};

export default SectionItems; 